/**
 * What the registry says every session is doing (RFC 0284 S8a).
 *
 * ONE DOCUMENT READ for the whole list, however long it is. That is the point
 * of the field: `useSessionStatuses` opens a session document per row, which
 * capped the list at twenty and took a renderer past its heap limit, and no
 * installed sidebar could ever pay that price.
 *
 * ONLY WHAT IT CAN ANSWER. A session with no fresh claim is absent from the
 * map rather than present as idle, so a caller can hand exactly those ids to
 * the expensive path and leave the rest alone.
 *
 * RECOMPUTED ON A TIMER as well as on change, because staleness is a function
 * of the clock rather than of the document: a running session whose writer died
 * has to stop being reported as running WITHOUT anything arriving to trigger a
 * re-read.
 *
 * THE TIMER ONLY RUNS WHEN SOMETHING CAN GO STALE. Only a non-idle claim
 * expires, so a World where nothing is running has nothing to re-check, and an
 * unconditional interval would re-render the whole shell every ten seconds
 * forever to discover that. Most windows are in that state most of the time.
 *
 * A MOD-CORE THAT PREDATES IT is caught before this runs, by `missingCoreApi`
 * in the shell. The guard belongs there rather than here: the app runs from a
 * dev server and mod-core from a BUILT package, so this file is one of many
 * that can hot-reload ahead of a rebuild, and a check per caller is a check
 * somebody forgets to add.
 */

import { projectSessionStatuses } from '@mod/core/model'
import { useEffect, useMemo, useState } from 'react'
import type { SessionStatusMap } from '../sessions/sessionStatus'

/**
 * How often to re-read for staleness alone.
 *
 * A third of the freshness window, so a claim is reported stale within a few
 * seconds of becoming so without polling hard. This costs one pure projection
 * over a map already in memory; it opens nothing.
 */
const STALENESS_TICK_MS = 10_000

export function useRegistryStatuses(projectDoc: unknown): SessionStatusMap {
  const [tick, setTick] = useState(0)

  const statuses = useMemo(() => {
    // `tick` is a SIGNAL, not an input: it exists so the projection re-runs
    // against a later clock when nothing about the document has changed.
    void tick
    return new Map(
      Object.entries(projectSessionStatuses(projectDoc, Date.now())),
    )
  }, [projectDoc, tick])

  // Only a non-idle claim can expire. Nothing running means nothing to wait
  // for, and the interval would otherwise re-render the shell every ten
  // seconds for the life of the window.
  const canExpire = useMemo(() => {
    for (const status of statuses.values()) if (status !== 'idle') return true
    return false
  }, [statuses])

  useEffect(() => {
    if (!canExpire) return
    const id = setInterval(() => setTick((n) => n + 1), STALENESS_TICK_MS)
    return () => clearInterval(id)
  }, [canExpire])

  return statuses
}
